import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../api/admin.api';
import { useAuthStore } from '../../store/auth.store';
import type {
  AdminUserDto,
  CreateAdminDto,
  CreateAdminResponseDto,
  UpdateAdminProvidersDto,
  CreateDeveloperDto,
  CreateDeveloperResponseDto,
  AdminChangePasswordDto,
  SuccessResponseDto,
  DeveloperListItemDto,
  DeveloperDetailDto,
  DeveloperDto,
  UpdateDeveloperDto,
  ResetPasswordResponseDto,
  DeveloperPermissionsResponseDto,
  SetDeveloperRoleResponseDto,
} from '../../types';
import type { AdminChangePasswordResponse, TotpSetupResponse, TotpEnableResponse } from '../../api/adminAuth';

export function useAdminAdmins(params?: { page?: number; limit?: number; search?: string; roleId?: string; providerId?: string }) {
  return useQuery<{
    items: AdminUserDto[];
    total: number;
    page: number;
    limit: number;
  }>({
    queryKey: ['admin', 'admins', params],
    queryFn: async () => {
      const { data } = await adminApi.get('/v1/admin/admins', { params });
      const raw = data.data || data;
      if (Array.isArray(raw)) {
        return {
          items: raw,
          total: raw.length,
          page: params?.page || 1,
          limit: params?.limit || 20,
        };
      }
      return {
        items: raw.items || raw.data || [],
        total: raw.total ?? (raw.items ? raw.items.length : 0),
        page: raw.page ?? (params?.page || 1),
        limit: raw.limit ?? (params?.limit || 20),
      };
    },
    staleTime: 30_000,
  });
}

export function useAdminAdminDetail(id: string) {
  return useQuery<AdminUserDto>({
    queryKey: ['admin', 'admin', id],
    queryFn: async () => {
      const { data } = await adminApi.get(`/v1/admin/admins/${id}`);
      return data.data || data;
    },
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useCreateAdminUser() {
  const queryClient = useQueryClient();
  return useMutation<CreateAdminResponseDto, Error, CreateAdminDto>({
    mutationFn: async (payload) => {
      const { data } = await adminApi.post('/v1/admin/admins', payload);
      return data.data || data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'admins'] });
    },
  });
}

export function useUpdateAdminProviders() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { id: string; payload: UpdateAdminProvidersDto }>({
    mutationFn: async ({ id, payload }) => {
      await adminApi.patch(`/v1/admin/admins/${id}/providers`, payload);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'admins'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'admin', variables.id] });
    },
  });
}

export function useDeleteAdminUser() {
  const queryClient = useQueryClient();
  return useMutation<SuccessResponseDto, Error, string>({
    mutationFn: async (id: string) => {
      const { data } = await adminApi.delete(`/v1/admin/admins/${id}`);
      return data.data || data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'admins'] });
    },
  });
}

export function useAdminDevelopers(params?: { provider_id?: string; page?: number; limit?: number; search?: string }) {
  return useQuery<DeveloperListItemDto[]>({
    queryKey: ['admin', 'developers', params],
    queryFn: async () => {
      const { data } = await adminApi.get('/v1/admin/developers', { params: { limit: 100, ...params } });
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.items)) return data.items;
      if (Array.isArray(data?.data?.items)) return data.data.items;
      if (Array.isArray(data?.data)) return data.data;
      return [];
    },
    staleTime: 30_000,
  });
}

export function useAdminDeveloper(id: string) {
  return useQuery<DeveloperDetailDto>({
    queryKey: ['admin', 'developer', id],
    queryFn: async () => {
      const { data } = await adminApi.get(`/v1/admin/developers/${id}`);
      return data.data || data;
    },
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useCreateDeveloperUser() {
  const queryClient = useQueryClient();
  return useMutation<CreateDeveloperResponseDto, Error, CreateDeveloperDto>({
    mutationFn: async (payload) => {
      const { data } = await adminApi.post('/v1/admin/developers', payload);
      return data.data || data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'developers'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'providers'] });
    },
  });
}

export function useUpdateDeveloperUser() {
  const queryClient = useQueryClient();
  return useMutation<DeveloperDto, Error, { id: string; data: UpdateDeveloperDto }>({
    mutationFn: async ({ id, data: payload }) => {
      const { data } = await adminApi.patch(`/v1/admin/developers/${id}`, payload);
      return data.data || data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'developers'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'developer', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'providers'] });
    },
  });
}

export function useDeleteDeveloperUser() {
  const queryClient = useQueryClient();
  return useMutation<SuccessResponseDto, Error, string>({
    mutationFn: async (id: string) => {
      const { data } = await adminApi.delete(`/v1/admin/developers/${id}`);
      return data.data || data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'developers'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'developer', id] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'providers'] });
    },
  });
}

export function useResetDeveloperPassword() {
  const queryClient = useQueryClient();
  return useMutation<ResetPasswordResponseDto, Error, string>({
    mutationFn: async (id: string) => {
      const { data } = await adminApi.patch(`/v1/admin/developers/${id}/reset-password`);
      return data.data || data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'developer', id] });
    },
  });
}

/**
 * Fetch application permissions held by a developer (Platform Admin only, Read-Only).
 * Endpoint: GET /v1/admin/developers/{id}/permissions
 */
export function useAdminDeveloperPermissions(developerId: string, options?: { enabled?: boolean }) {
  return useQuery<DeveloperPermissionsResponseDto>({
    queryKey: ['admin', 'developer-permissions', developerId],
    queryFn: async () => {
      const { data } = await adminApi.get(`/v1/admin/developers/${developerId}/permissions`);
      return data.data || data;
    },
    enabled: !!developerId && (options?.enabled ?? true),
    staleTime: 30_000,
  });
}

/**
 * Promote or demote a developer's role between super and member (Platform Admin only).
 * Endpoint: PATCH /v1/admin/developers/{id}/role
 */
export function useAdminSetDeveloperRole() {
  const queryClient = useQueryClient();
  return useMutation<SetDeveloperRoleResponseDto, Error, { id: string; role: 'super' | 'member' }>({
    mutationFn: async ({ id, role }) => {
      const { data } = await adminApi.patch(`/v1/admin/developers/${id}/role`, { role });
      return data.data || data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'developers'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'developer', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'developer-permissions', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'providers'] });
    },
  });
}

export function useChangeAdminPassword() {
  return useMutation<AdminChangePasswordResponse, Error, AdminChangePasswordDto>({
    mutationFn: async (payload) => {
      const { data } = await adminApi.patch('/v1/admin/auth/password', payload);
      return data.data || data;
    },
    onSuccess: (res) => {
      // Every previous token (this session included) is revoked — the new pair must be stored.
      const store = useAuthStore.getState();
      if (res?.accessToken && res?.refreshToken) {
        store.setTokens(res.accessToken, res.refreshToken);
      }
      store.setSecurityFlags({ mustChangePassword: false });
    },
  });
}

// ─── Admin TOTP (2FA) ─────────────────────────────────────────────────────────

export function useAdminTotpSetup() {
  return useMutation<TotpSetupResponse, Error, { currentPassword: string }>({
    mutationFn: async (payload) => {
      const { data } = await adminApi.post('/v1/admin/auth/totp/setup', payload);
      return data.data || data;
    },
  });
}

export function useAdminTotpEnable() {
  return useMutation<TotpEnableResponse, Error, { code: string }>({
    mutationFn: async (payload) => {
      const { data } = await adminApi.post('/v1/admin/auth/totp/enable', payload);
      return data.data || data;
    },
    onSuccess: () => {
      // The current access token stays valid; the next login gets the TOTP step.
      // mustEnrollTotp is cleared by the caller once the recovery codes have been acknowledged.
      useAuthStore.getState().setSecurityFlags({ totpEnabled: true });
    },
  });
}

export function useAdminTotpDisable() {
  const queryClient = useQueryClient();
  return useMutation<SuccessResponseDto, Error, { currentPassword: string; code: string }>({
    mutationFn: async (payload) => {
      const { data } = await adminApi.post('/v1/admin/auth/totp/disable', payload);
      return data.data || data;
    },
    onSuccess: () => {
      // Every session is revoked, including this one.
      useAuthStore.getState().logout();
      queryClient.clear();
    },
  });
}

/** Reset another admin's TOTP (permission `admin.totp.reset`). Signs that admin out everywhere. */
export function useResetAdminTotp() {
  const queryClient = useQueryClient();
  return useMutation<SuccessResponseDto, Error, string>({
    mutationFn: async (id) => {
      const { data } = await adminApi.post(`/v1/admin/admins/${id}/totp/reset`);
      return data.data || data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'admins'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'admin', id] });
    },
  });
}

export function useAdminLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { refreshToken } = useAuthStore.getState();
      if (refreshToken) {
        const { data } = await adminApi.post('/v1/admin/auth/logout', { refreshToken });
        return data;
      }
    },
    onSettled: () => {
      useAuthStore.getState().logout();
      queryClient.clear();
    },
  });
}


